import { useEffect } from 'react';

import { Skeleton, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import { sanitizeHTML } from 'utils/helpers';

import { getMessageTemplates } from 'services/suggestedMessages';

import { TriggersI18nKeys } from 'components/MessageTipsDrawer/constants';
import { TemplatesListPreviewProps } from 'components/MessageTipsDrawer/types';

import * as S from '../../styles';

export const TemplatesListPreview: React.FC<TemplatesListPreviewProps> = ({
  triggerCode,
  setIsErrorSnackOpen,
}) => {
  const intl = useIntl();

  const {
    data: messageTemplates,
    isLoading,
    isError,
  } = useQuery({
    queryFn: () => {
      return triggerCode // double checking since the `enabled` option does not guarantee type safety
        ? getMessageTemplates(triggerCode)
        : Promise.reject(new Error('triggerCode can not be empty'));
    },
    queryKey: ['messageTemplates', triggerCode],
    enabled: !!triggerCode,
  });

  useEffect(() => {
    if (isError) {
      setIsErrorSnackOpen(true);
    }
  }, [isError, setIsErrorSnackOpen]);

  if (!triggerCode || isLoading || !Array.isArray(messageTemplates)) {
    return (
      <>
        <Skeleton
          variant="text"
          width="100%"
          height={20}
          animation="wave"
          sx={{ mt: 2 }}
        />
        <Skeleton
          variant="text"
          width="100%"
          height={20}
          animation="wave"
          sx={{ mt: 3 }}
        />
        <Skeleton
          variant="rounded"
          width="100%"
          height={180}
          animation="wave"
          sx={{ mt: 2 }}
        />
      </>
    );
  }

  return (
    <>
      <Typography variant="caption" sx={{ mt: 2 }}>
        {intl.formatMessage(
          {
            id: TriggersI18nKeys[triggerCode].description,
          },
          {
            b: (msg: string) => (
              <Typography variant="caption" component="span" fontWeight={500}>
                {msg}
              </Typography>
            ),
          },
        )}
      </Typography>

      <Typography variant="caption" sx={{ mt: 3 }} color="text.secondary">
        {intl.formatMessage({
          id: TriggersI18nKeys[triggerCode].subtitle,
        })}
      </Typography>

      {messageTemplates?.map(mt => (
        <S.MessagePreviewContainer
          key={mt.id}
          data-testid="messagePreviewContainer"
        >
          <S.MessagePreviewText
            dangerouslySetInnerHTML={sanitizeHTML(mt.message, {
              ALLOWED_TAGS: ['b'],
              ALLOWED_ATTR: [],
            })}
          />
        </S.MessagePreviewContainer>
      ))}
    </>
  );
};
